import type { MediaAsset } from '-/Custom/Util.types';
import * as ImagePicker from 'expo-image-picker';
import { useLocalSearchParams } from 'expo-router';
import { type RefObject, useRef, useState, useSyncExternalStore } from 'react';
import type { FlatList } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useMutation, useQuery } from 'urql';
import { v4 } from 'uuid';
import { shallow } from 'zustand/shallow';
import { useChatDetailContext } from '@/components/Chat/ChatDetailContext';
import {
  type FragmentOf,
  graphql,
  type ResultOf,
  readFragment,
} from '@/gql/graphql';
import useGenerateImageUploadUrls from '@/hooks/useGenerateImageUploadUrls';
import useUploadMessageImage from '@/hooks/useUploadMessageImage';
import useUserId from '@/hooks/useUserId';
import ChatInputText from '@/screens/ChatDetail/components/ChatInputText';
import ChatMediaInputBar from '@/screens/ChatDetail/components/ChatMediaInputBar';
import ChatMentionSuggestions, {
  ChatMentionSuggestionFragment,
} from '@/screens/ChatDetail/components/ChatMentionSuggestions';
import useAuthStore from '@/stores/useAuthStore';
import { setChatMediaCacheAsset } from '@/stores/useChatMediaCacheStore';
import {
  addChatMedia,
  clearChatInput,
  clearChatMedia,
  setChatInput,
  setUploadMediaMeta,
  useChatStore,
} from '@/stores/useChatStore';
import { persistChatMediaFile } from '@/utils/chatMediaCache';
import getMentionSuggestions from '@/utils/getMentionSuggestions';
import isDesktopUserAgent from '@/utils/isDesktopUserAgent';
import { serializeMessageEditorValue } from '@/utils/messageMentions';
import Box from '../Unistyles/Box';
import Button from '../Unistyles/Button';
import Icon from '../Unistyles/Icon';
import Text from '../Unistyles/Text';
import { ChatMessageItemFragment } from './ChatMessageItem.utils';

async function persistChatInputMediaAsset({
  asset,
  chatId,
}: {
  asset: MediaAsset;
  chatId: string;
}) {
  if (!asset.path) {
    return;
  }

  const fallbackAsset = {
    id: asset.id,
    chatId,
    path: asset.path,
    uploadUri: asset.path,
    storageUri: null,
    mimeType: asset.mimeType ?? null,
  };

  setChatMediaCacheAsset(fallbackAsset);

  try {
    const persisted = await persistChatMediaFile({
      id: asset.id,
      uri: asset.path,
      mimeType: asset.mimeType,
    });

    setChatMediaCacheAsset({
      id: asset.id,
      chatId,
      path: persisted.previewUri || persisted.storageUri || asset.path || '',
      storageUri: persisted.storageUri,
      uploadUri: persisted.uploadUri || asset.path,
      mimeType: persisted.mimeType,
    });
  } catch {
    setChatMediaCacheAsset(fallbackAsset);
  }
}

export const InsertMessage = graphql(/* GraphQL */ `
  mutation InsertMessage(
    $id: uuid!
    $chatId: uuid!
    $content: String
    $contentTemplate: String
    $mentions: message_mention_arr_rel_insert_input!
    $media: message_media_arr_rel_insert_input!
  ) {
    insert_message_one(
      object: {
        id: $id
        chat_id: $chatId
        content: $content
        content_template: $contentTemplate
        mentions: $mentions
        media: $media
      }
    ) {
      chat {
        chat_type
        id
        is_hidden
        preference {
          chat_id
          person_id
          notification_preference
        }
        last_message_at
        metadata
        unseen_message_count
      }
      ...ChatMessageItem
    }
  }
`, [ChatMessageItemFragment]);

const UpdateMessage = graphql(/* GraphQL */ `
  mutation UpdateMessage(
    $id: uuid!
    $content: String!
    $contentTemplate: String
    $mentionIds: [uuid!]!
    $mentions: [message_mention_insert_input!]!
  ) {
    clearMessageTemplate: update_message_by_pk(
      pk_columns: { id: $id }
      _set: { content_template: null }
    ) {
      id
    }
    prepareMessageUpdate: update_message_by_pk(
      pk_columns: { id: $id }
      _set: {
        content: $content
        content_template: $contentTemplate
        edited_at: "now()"
      }
    ) {
      id
    }
    delete_message_mention(
      where: { message_id: { _eq: $id }, id: { _nin: $mentionIds } }
    ) {
      affected_rows
    }
    insert_message_mention(
      objects: $mentions
      on_conflict: { constraint: message_mention_pkey, update_columns: [] }
    ) {
      affected_rows
    }
    update_message_by_pk(
      pk_columns: { id: $id }
      _set: {
        content: $content
        content_template: $contentTemplate
        edited_at: "now()"
      }
    ) {
      ...ChatMessageItem
    }
  }
`, [ChatMessageItemFragment]);

const GetChatMentionPeople = graphql(/* GraphQL */ `
  query GetChatMentionPeople($chatId: uuid!) {
    chat_by_pk(id: $chatId) {
      id
      chat_type
      participants {
        ...ChatMentionSuggestion
      }
    }
  }
`, [ChatMentionSuggestionFragment]);

const MESSAGE_MAX_LENGTH = 2048;
const MESSAGE_LENGTH_ERROR = 'Messages must be 2,048 characters or fewer.';

const keepEditorFocused:
  | { onMouseDown: (event: { preventDefault(): void }) => void }
  | undefined =
  process.env.EXPO_OS === 'web'
    ? { onMouseDown: (event) => event.preventDefault() }
    : undefined;

type ChatInputProps = {
  messages: readonly FragmentOf<typeof ChatMessageItemFragment>[];
  listRef: RefObject<FlatList<
    FragmentOf<typeof ChatMessageItemFragment>
  > | null>;
};

function ChatInput(props: ChatInputProps) {
  const hasHydrated = useSyncExternalStore(
    useChatStore.persist.onFinishHydration,
    useChatStore.persist.hasHydrated,
    () => false,
  );
  return hasHydrated ? <ChatInputComposer {...props} /> : null;
}

function ChatInputComposer({ listRef, messages }: ChatInputProps) {
  const userId = useUserId();
  // TODO Fix types here, as casting it as an empty string is a bit of a hack, and not type-safe.
  const { chatId: id = '' } = useLocalSearchParams<{ chatId: string }>();
  const draft = useChatStore((state) => state.chatInput[id]);
  const chatInput = draft?.text;
  const isMessageTooLong =
    Array.from(chatInput?.trim() ?? '').length > MESSAGE_MAX_LENGTH;
  const editedMessageId = draft?.editedMessageId;
  const chatMediaItem = useChatStore((state) => state.chatMedia.get(id));
  const chatMediaItemArray = Array.from(chatMediaItem || []);
  const [, insertMessage] = useMutation(InsertMessage);
  const [, updateMessage] = useMutation(UpdateMessage);
  const isSubmittingRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComposerFocused, setIsComposerFocused] = useState(false);
  const [mentionSearch, setMentionSearch] = useState<string | null>(null);
  const [selectedMentionPersonId, setSelectedMentionPersonId] = useState<
    string | null
  >(null);
  const [{ data: mentionPeopleData }] = useQuery({
    query: GetChatMentionPeople,
    variables: { chatId: id },
    pause: mentionSearch === null,
  });
  const generateImageUploadUrls = useGenerateImageUploadUrls();

  const { isAtNewest, inputRef } = useChatDetailContext(
    (state) => ({
      isAtNewest: state.isAtNewest,
      inputRef: state.inputRef,
    }),
    shallow,
  );

  const uploadMessageImage = useUploadMessageImage();

  const handleAddAssets = async (assets?: ImagePicker.ImagePickerAsset[]) => {
    if (!assets?.length) {
      return;
    }

    for (const asset of assets) {
      if (!asset?.uri) {
        continue;
      }

      const messageMediaId = v4();

      addChatMedia(id, {
        id: messageMediaId,
        path: asset.uri,
        mimeType: asset.mimeType ?? null,
      });
    }
  };

  const onPressSubmit = async () => {
    const editor = inputRef.current;
    if (isSubmittingRef.current || isMessageTooLong || !editor) return;
    const submittingUserId = useAuthStore.getState().uid;
    const updateEditedMessageId = editedMessageId;
    const plainContent =
      useChatStore.getState().chatInput[id]?.text.trim() ?? '';
    const mediaInsert = updateEditedMessageId
      ? []
      : chatMediaItemArray.map((item, index) => ({
          id: item.id,
          media_id: null,
          order: index,
          chat_id: id,
        }));
    if (!plainContent && mediaInsert.length === 0) return;

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    let html: string;
    try {
      html = await editor.getHTML();
    } catch {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      return;
    }
    if (useAuthStore.getState().uid !== submittingUserId) return;
    const content = useChatStore.getState().chatInput[id]?.text.trim() ?? '';
    if (Array.from(content).length > MESSAGE_MAX_LENGTH) {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      return;
    }
    const message = serializeMessageEditorValue({ html, text: content });
    const messageId = v4();
    const persistedMedia = updateEditedMessageId
      ? []
      : chatMediaItemArray.map((asset) =>
          persistChatInputMediaAsset({ asset, chatId: id }),
        );

    await new Promise<void>((resolve) => {
      setTimeout(() => {
        if (useAuthStore.getState().uid === submittingUserId) {
          clearChatInput(id);
          editor.setValue('');
          if (!updateEditedMessageId) clearChatMedia(id);
        }
        resolve();
      }, 0);
    });
    if (useAuthStore.getState().uid !== submittingUserId) return;

    const insertContent = message.content || null;
    let saved = false;
    try {
      if (updateEditedMessageId) {
        const { error } = await updateMessage({
          id: updateEditedMessageId,
          content: message.content,
          contentTemplate: message.contentTemplate,
          mentionIds: message.mentions.map((mention) => mention.id),
          mentions: message.mentions.map((mention) => ({
            ...mention,
            message_id: updateEditedMessageId,
          })),
        });
        saved = !error;
      } else {
        const { error } = await insertMessage({
          id: messageId,
          chatId: id,
          content: insertContent,
          contentTemplate: message.contentTemplate,
          mentions: { data: message.mentions },
          media: { data: mediaInsert },
        });
        saved = !error;
      }
    } catch {}
    if (useAuthStore.getState().uid !== submittingUserId) return;
    isSubmittingRef.current = false;
    setIsSubmitting(false);
    if (!saved) return;
    if (updateEditedMessageId) return;
    mediaInsert.forEach((item) => {
      setUploadMediaMeta(item.id, { isUploading: true });
    });

    if (chatMediaItemArray.length) {
      await Promise.all(persistedMedia);

      const { data: uploadUrls, error: uploadUrlsError } =
        await generateImageUploadUrls(chatMediaItemArray.length);

      if (
        !uploadUrlsError &&
        uploadUrls?.generateImageUploadUrls?.length === mediaInsert.length
      ) {
        const promises = mediaInsert.map((item, index) =>
          uploadMessageImage(
            item.id,
            `message/${id}/${item.id}`,
            uploadUrls.generateImageUploadUrls?.[index]?.uploadURL || '',
          ),
        );

        await Promise.allSettled(promises);
      } else {
        mediaInsert.forEach((item) => {
          setUploadMediaMeta(item.id, { isUploading: false });
        });
      }
    }

    if (!isAtNewest) {
      listRef?.current?.scrollToIndex({ index: 0 });
    }
  };

  const onPressCamera = async () => {
    await ImagePicker.requestCameraPermissionsAsync();
    const res = await ImagePicker.launchCameraAsync();

    await handleAddAssets(res.assets ?? undefined);
  };

  const onPressLibrary = async () => {
    const res = await ImagePicker.launchImageLibraryAsync({
      allowsMultipleSelection: true,
      selectionLimit: 5,
    });

    await handleAddAssets(res.assets ?? undefined);
  };

  const submitIconName = editedMessageId ? 'checkmark' : 'arrow-up-outline';
  const mentionPeople = readFragment(
    ChatMentionSuggestionFragment,
    mentionPeopleData?.chat_by_pk?.chat_type === 'dm'
      ? []
      : (mentionPeopleData?.chat_by_pk?.participants ?? []),
  );
  const recentSenderIds = readFragment(
    ChatMessageItemFragment,
    messages,
  ).flatMap((message) =>
    !message.deleted_at && message.creator?.id ? [message.creator.id] : [],
  );
  const matchingMentionPeople = getMentionSuggestions(
    mentionPeople,
    recentSenderIds,
    userId,
    mentionSearch ?? '',
  );
  const mentionSuggestionsVisible =
    isComposerFocused &&
    mentionSearch !== null &&
    matchingMentionPeople.length > 0;
  const matchingSelectedMentionPersonIndex = matchingMentionPeople.findIndex(
    (person) => person.id === selectedMentionPersonId,
  );
  const selectedMentionPersonIndex =
    matchingSelectedMentionPersonIndex < 0
      ? 0
      : matchingSelectedMentionPersonIndex;
  const selectedMentionPerson =
    matchingMentionPeople[selectedMentionPersonIndex];

  const selectMentionPerson = (
    person: ResultOf<typeof ChatMentionSuggestionFragment>,
  ) => {
    const fallbackLabel = person.name ?? '';
    inputRef.current?.setMention('@', `@${fallbackLabel}`, {
      id: person.id,
      'data-fallback-label': fallbackLabel,
    });
    setMentionSearch(null);
    setSelectedMentionPersonId(null);
  };

  const selectActiveMentionPerson = () => {
    if (
      !isComposerFocused ||
      mentionSearch === null ||
      !selectedMentionPerson
    ) {
      return false;
    }

    selectMentionPerson(selectedMentionPerson);
    return true;
  };

  const handleMentionKeyPress = (key: string) => {
    if (
      !isComposerFocused ||
      mentionSearch === null ||
      matchingMentionPeople.length === 0
    ) {
      return false;
    }

    if (key === 'Tab') {
      return selectActiveMentionPerson();
    }

    if (key !== 'ArrowDown' && key !== 'ArrowUp') {
      return false;
    }

    const direction = key === 'ArrowDown' ? 1 : -1;
    const nextIndex =
      (selectedMentionPersonIndex + direction + matchingMentionPeople.length) %
      matchingMentionPeople.length;
    setSelectedMentionPersonId(matchingMentionPeople[nextIndex]?.id ?? null);
    return true;
  };

  return (
    <Box style={style.container}>
      {isMessageTooLong ? (
        <Text variant="error">{MESSAGE_LENGTH_ERROR}</Text>
      ) : null}
      <ChatMediaInputBar />

      <Box style={style.mentionOverlay}>
        {mentionSuggestionsVisible ? (
          <ChatMentionSuggestions
            people={matchingMentionPeople}
            selectedPersonId={selectedMentionPerson?.id ?? null}
            onSelect={selectMentionPerson}
          />
        ) : null}
      </Box>

      <Box alignItems="center" direction="horizontal" space="md" m="md">
        {process.env.EXPO_OS !== 'web' ? (
          <Button
            iconOnly
            variant="secondary"
            outline
            style={style.toolbarButton}
            onPress={onPressCamera}
          >
            <Icon name="camera-outline" size="lg" />
          </Button>
        ) : null}

        <Button
          iconOnly
          variant="secondary"
          outline
          style={style.toolbarButton}
          onPress={onPressLibrary}
          accessibilityLabel="Add photos"
        >
          <Icon name="image-outline" size="lg" />
        </Button>

        <Box style={style.inputContainer}>
          <ChatInputText
            key={id}
            defaultValue={draft?.html ?? chatInput ?? ''}
            inputRef={inputRef}
            mentionSuggestionsVisible={mentionSuggestionsVisible}
            selectedMentionPersonId={selectedMentionPerson?.id ?? null}
            onMentionKeyPress={handleMentionKeyPress}
            onMentionSearchChange={(value) => {
              setMentionSearch(value);
              setSelectedMentionPersonId(null);
            }}
            onMentionSubmit={selectActiveMentionPerson}
            onChangeText={(value) => {
              setChatInput(id, { text: value });
            }}
            onChangeHtml={(html) => setChatInput(id, { html })}
            onFocusChange={setIsComposerFocused}
            onSubmit={() => {
              if (isDesktopUserAgent) {
                void onPressSubmit();
              }
            }}
          />
        </Box>
        {editedMessageId ? (
          <Button
            iconOnly
            {...keepEditorFocused}
            accessibilityLabel="Stop editing"
            variant="secondary"
            outline
            style={style.toolbarButton}
            disabled={isSubmitting}
            onPress={() => {
              clearChatInput(id);
              inputRef.current?.setValue('');
            }}
          >
            <Icon name="close-outline" size="xl" />
          </Button>
        ) : null}
        {chatInput || chatMediaItemArray.length ? (
          <Button
            iconOnly
            {...keepEditorFocused}
            style={style.submitButton}
            disabled={isSubmitting || isMessageTooLong}
            onPress={onPressSubmit}
            testID="ChatInputSendButton"
            variant="primary"
          >
            <Icon variant="white" name={submitIconName} size="xl" />
          </Button>
        ) : null}
      </Box>
    </Box>
  );
}

const style = StyleSheet.create((theme) => ({
  container: {
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: theme.colors.borderMuted,
  },
  mentionOverlay: {
    height: 0,
    position: 'relative',
    width: '100%',
    zIndex: 1000,
  },
  inputContainer: {
    flex: 1,
    borderRadius: theme.borderRadius['2xl'],
    overflow: 'hidden',
  },
  toolbarButton: {
    width: theme.size(10),
    height: theme.size(10),
    padding: theme.space('md'),
    borderRadius: theme.borderRadius.full,
    borderColor: theme.colors.borderMuted,
    alignItems: 'center',
    justifyContent: 'center',
  },
  submitButton: {
    width: theme.size(10),
    height: theme.size(10),
    padding: theme.space('md'),
    borderRadius: theme.borderRadius.full,
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

export default ChatInput;
